/** /tag-schemas/:id/:tab? — tag schema editor shell. Tabs: General, Schema, Used by. */
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as tagSchemasApi from '@/api/tagSchemas';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { act, DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';

import { TagSchemaEditorTab } from './tabs/TagSchemaEditorTab';
import { TagSchemaGeneralTab } from './tabs/TagSchemaGeneralTab';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'schema', label: 'Schema' },
  { id: 'used-by', label: 'Used by' },
] as const;

export function TagSchemaDetailPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const id = Number(idStr);
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    data: schema,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tag_schemas', 'detail', id],
    queryFn: () => tagSchemasApi.retrieve(id),
    enabled: Number.isFinite(id),
  });
  const onSaved = () => {
    void refetch();
    qc.invalidateQueries({ queryKey: ['tag_schemas'] });
  };

  const deleteMutation = useMutation({
    mutationFn: () => tagSchemasApi.remove(id),
    onSuccess: () => {
      snackbar.show('Schema deleted.', 'success');
      qc.invalidateQueries({ queryKey: ['tag_schemas'] });
      navigate('/tag-schemas');
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  const activeTab = useMemo(() => TABS.find((t) => t.id === tab)?.id ?? 'general', [tab]);

  if (!Number.isFinite(id)) return <Alert severity="warning">Invalid schema id.</Alert>;
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !schema) return <Alert severity="error">Couldn’t load tag schema.</Alert>;

  const referenced = schema.used_by.categories + schema.used_by.set_items;

  return (
    <Box>
      <PageHeader
        helpStory="tag-schemas"
        section={TABS.find((t) => t.id === activeTab)?.label}
        title={schema.name}
        subtitle={
          <DetailSubtitle
            parts={[
              <ValueActions key="codename" actions={[act.copy(schema.codename, 'Copy codename')]}>
                {schema.codename}
              </ValueActions>,
              <ValueActions key="id" actions={[act.copy(String(schema.id), 'Copy id')]}>ID {schema.id}</ValueActions>,
            ]}
          />
        }
        actions={
          <PermissionGate perm="console_tag_schemas_manage">
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={referenced > 0}
              title={
                referenced > 0 ? 'In use — repoint referencing categories/sets first.' : undefined
              }
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </Button>
          </PermissionGate>
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => navigate(`/tag-schemas/${id}/${v}`)}>
          {TABS.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <TabPane show={activeTab === 'general'}>
        <TagSchemaGeneralTab schema={schema} onSaved={onSaved} />
      </TabPane>
      <TabPane show={activeTab === 'schema'}>
        <TagSchemaEditorTab schema={schema} onSaved={onSaved} />
      </TabPane>
      <TabPane show={activeTab === 'used-by'}>
        <UsedBy categories={schema.used_by.categories} setItems={schema.used_by.set_items} />
      </TabPane>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        <RouterLink to="/tag-schemas" style={{ color: '#0a1a3f' }}>
          ← Back to tag schemas
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this tag schema?"
        message={`"${schema.name}" will be deleted. This cannot be undone.`}
        confirmLabel="Delete schema"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}

function UsedBy({ categories, setItems }: { categories: number; setItems: number }) {
  const total = categories + setItems;
  return (
    <Stack spacing={1} sx={{ maxWidth: 520 }}>
      <Typography variant="body2" color="text.secondary">
        Where this schema is referenced (via <code>schema_ref</code>).
      </Typography>
      <Typography variant="body2">
        Tag categories: <strong>{categories}</strong>
      </Typography>
      <Typography variant="body2">
        Tag-category-set items: <strong>{setItems}</strong>
      </Typography>
      {total === 0 && (
        <Typography variant="body2" color="text.secondary">
          Not referenced anywhere — safe to delete.
        </Typography>
      )}
    </Stack>
  );
}

function TabPane({ show, children }: { show: boolean; children: ReactNode }) {
  if (!show) return null;
  return <Box>{children}</Box>;
}
